import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import BulkWalkthroughsDialogueSkeleton from 'src/components/BulkOperations/_Shared/skeletons/BulkWalkthroughsDialogueSkeleton';
import Button from 'src/components/Button';
import BulkAddKeyPickups from 'src/components/BulkOperations/KeyPickups/BulkAddKeyPickups/BulkAddKeyPickups';
import BulkKeyPickupSummary from 'src/components/BulkOperations/KeyPickups/BulkKeyPikcupSummary/BulkKeyPikcupSummary';
import { usePropertySuitesBulkDialogues } from 'src/SharedCustomHooks/UsePropertySuites/usePropertySuitesBulkDialogues';

interface BulkAddKeyPickupsProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  selectedSuiteIds: string[];
  onSave?: (params: any) => void;
  propertyId: any;
  isLoading: any;
  onSetBulkSuccess: () => void;
  bulkKeyPickupsCreated: any;
}

export const BulkAddKeyPickupsDialogue = ({
  isOpen,
  onClose,
  userId,
  selectedSuiteIds,
  onSave,
  propertyId,
  isLoading,
  onSetBulkSuccess,
  bulkKeyPickupsCreated,
}: BulkAddKeyPickupsProps) => {
  const {
    suites,
    isLoading: suitesLoading,
    error: suitesError,
    refetch: refetchSuites,
  } = usePropertySuitesBulkDialogues({
    propertyId,
    userId,
    enabled: isOpen,
  });

  const handleClose = () => {
    onSetBulkSuccess();
    onClose();
  };

  const handleRetry = () => {
    refetchSuites();
  };

  const dialogueMinHeight: string = '80vh';

  if (suitesLoading) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Add Key Pickups"
        description="Add key pickups to multiple suites at once"
        minHeight={dialogueMinHeight}
      >
        <BulkWalkthroughsDialogueSkeleton />
      </GenericDialogue>
    );
  }

  if (suitesError) {
    return (
      <GenericDialogue
        isOpen={isOpen}
        onClose={handleClose}
        title="Bulk Add Key Pickups"
        description="Add key pickups to multiple suites at once"
        minHeight={dialogueMinHeight}
      >
        <div className="bulk-add-key-pickups__error">
          <p>Error: {suitesError}</p>
          <Button
            color="teal"
            size="small"
            label="Retry"
            onClick={handleRetry}
          />
        </div>
      </GenericDialogue>
    );
  }

  return (
    <GenericDialogue
      isOpen={isOpen}
      onClose={handleClose}
      title={
        bulkKeyPickupsCreated.length > 0
          ? 'Key Pickups Configured'
          : 'Bulk Add Key Pickups'
      }
      description={
        bulkKeyPickupsCreated.length > 0
          ? 'Summary of configured key pickups'
          : 'Add key pickups to multiple suites at once'
      }
      minHeight={dialogueMinHeight}
    >
      {bulkKeyPickupsCreated.length > 0 ? (
        <BulkKeyPickupSummary createdKeyPickups={bulkKeyPickupsCreated} onClose={handleClose} />
      ) : (
        <BulkAddKeyPickups
          userId={userId}
          selectedSuiteIds={selectedSuiteIds}
          onSave={onSave}
          suites={suites}
          propertyId={propertyId}
          isSubmitting={isLoading}
        />
      )}
    </GenericDialogue>
  );
};

export default BulkAddKeyPickupsDialogue;
